// RHL: fresh cross-collection selections, real review priority, bounded lazy requests.
const CACHE_KEY = 'rhl-recommendation-pools-v3';
const SEEN_KEY = 'rhl-recommendation-seen-v1';
const TTL = 10 * 60 * 1000;
export class BatchHistory {
  constructor() { this.batches = []; this.index = -1; }
  push(batch) {
    this.batches = [...this.batches.slice(0, this.index + 1), batch].slice(-10);
    this.index = this.batches.length - 1;
  }
  get canGoBack() { return this.index > 0; }
  back() { return this.canGoBack ? this.batches[--this.index] : null; }
}
function read(key, fallback) {
  try { return JSON.parse(sessionStorage.getItem(key)) || fallback; } catch (_) { return fallback; }
}
function write(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* Storage is optional. */ }
}
function random() {
  if (globalThis.crypto?.getRandomValues) {
    return crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296;
  }
  return Math.random();
}
function group(type) {
  if (/wall|sconce|picture/.test(type)) return 'wall-sconce';
  if (/table|desk/.test(type)) return 'table-lamp';
  if (/floor/.test(type)) return 'floor-lamp';
  if (/outdoor|landscape/.test(type)) return 'outdoor-lighting';
  if (/fan/.test(type)) return 'ceiling-fan';
  if (/flush|ceiling/.test(type)) return 'ceiling-lamp';
  if (/pendant/.test(type)) return 'pendant-light';
  if (/chandelier/.test(type)) return 'chandelier';
  return type || 'other';
}
// Exported to exercise distribution, deduplication and exhausted-pool behaviour offline.
export function selectProducts(rows, limit, current, seen = [], rng = random) {
  const unique = new Map();
  for (const row of rows) {
    if (Array.isArray(row) && /^[a-z0-9-]+$/.test(row[0]) && row[0] !== current) {
      unique.set(row[0], [row[0], group(row[1] || ''), row[2] === 1 ? 1 : 0]);
    }
  }
  const candidates = [...unique.values()], recent = new Set(seen), chosen = [], counts = {};
  const quota = [1, 1, 0, 1, 1, 0, 1, 1, 0, 1];
  while (chosen.length < Math.min(10, limit) && candidates.length) {
    const fresh = candidates.filter(p => !recent.has(p[0]));
    const available = fresh.length ? fresh : candidates;
    const preferred = available.filter(p => p[2] === quota[chosen.length]);
    const pool = preferred.length ? preferred : available;
    const least = Math.min(...pool.map(p => counts[p[1]] || 0));
    const diverse = pool.filter(p => (counts[p[1]] || 0) === least);
    const pick = diverse[Math.min(diverse.length - 1, Math.floor(rng() * diverse.length))];
    chosen.push(pick);
    counts[pick[1]] = (counts[pick[1]] || 0) + 1;
    candidates.splice(candidates.findIndex(p => p[0] === pick[0]), 1);
  }
  return chosen;
}
async function getHTML(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 12000);
  try {
    const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error('Recommendation request failed');
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  } finally { clearTimeout(timer); }
}
async function getPool(url) {
  const cache = read(CACHE_KEY, []).filter(p => p && Date.now() - p.time < TTL);
  const hit = cache.find(p => p.url === url);
  if (hit) return hit.rows;
  const doc = await getHTML(url);
  const data = JSON.parse(doc.querySelector('[data-rhl-pool]')?.textContent || 'null');
  if (!Array.isArray(data?.products)) throw new Error('Missing recommendation metadata');
  const rows = data.products;
  // Merge again after awaiting so concurrent metadata requests do not overwrite each other.
  const latest = read(CACHE_KEY, []).filter(p => p && p.url !== url && Date.now() - p.time < TTL);
  write(CACHE_KEY, [...latest, { url, rows, time: Date.now() }].slice(-6));
  return rows;
}
function imageArrows(scope) {
  for (const host of scope.querySelectorAll('[data-imgs]')) {
    if (host.__rhlNav) continue;
    const urls = (host.dataset.imgs || '').split('|').map(s => s.trim()).filter(Boolean);
    const main = host.querySelector('.hdt-card-product__media--main');
    const media = host.closest('.hdt-card-product__media');
    if (urls.length < 2 || !main || !media) continue;
    host.__rhlNav = true;
    host.closest('.hdt-card-product__wrapper')?.classList.add('rhl-cardnav-on');
    let index = 0;
    for (const direction of [-1, 1]) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rhl-card-image-nav rhl-card-image-nav--' + (direction < 0 ? 'prev' : 'next');
      button.setAttribute('aria-label', direction < 0 ? 'Previous image' : 'Next image');
      button.textContent = direction < 0 ? '‹' : '›';
      button.addEventListener('click', event => {
        event.preventDefault(); event.stopPropagation();
        index = (index + direction + urls.length) % urls.length;
        const src = new URL(urls[index], location.origin);
        main.removeAttribute('data-srcset');
        main.srcset = [360, 540, 720, 1080].map(w => {
          const variant = new URL(src); variant.searchParams.set('width', w);
          return variant.href + ' ' + w + 'w';
        }).join(',');
        main.loading = 'eager'; main.src = src.href;
      });
      media.appendChild(button);
    }
  }
}
if (typeof window !== 'undefined' && !customElements.get('rhl-random-recommendations')) {
  class RandomRecommendations extends HTMLElement {
    connectedCallback() {
      this.history ||= new BatchHistory();
      this.onControlClick = event => {
        const button = event.target.closest('[data-rhl-refresh], [data-rhl-previous]');
        if (!button || !this.contains(button) || this.busy) return;
        if (button.hasAttribute('data-rhl-refresh')) this.load();
        else {
          const batch = this.history.back();
          if (batch) {
            this.showBatch(batch);
            this.announce('Previous selection restored.');
            this.updateControls();
          }
        }
      };
      this.addEventListener('click', this.onControlClick);
      this.refreshOnReturn = event => { if (event.persisted) this.observe(); };
      window.addEventListener('pageshow', this.refreshOnReturn);
      this.observe();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.removeEventListener('click', this.onControlClick);
      window.removeEventListener('pageshow', this.refreshOnReturn);
    }
    announce(message) {
      const status = this.querySelector('[data-rhl-status]');
      if (status) status.textContent = message;
    }
    updateControls() {
      const toolbar = this.querySelector('[data-rhl-toolbar]');
      if (toolbar) toolbar.hidden = !this.querySelector(':scope > hdt-slider');
      const previous = this.querySelector('[data-rhl-previous]');
      if (previous) previous.disabled = this.busy || !this.history.canGoBack;
      const refresh = this.querySelector('[data-rhl-refresh]');
      if (refresh) {
        refresh.disabled = !!this.busy;
        refresh.querySelector('span').textContent = this.busy ? 'Loading…' : 'Refresh picks';
      }
    }
    showBatch(batch) {
      // Snapshots stay inert: restoring never makes a request or clones initialized slider state.
      const slider = batch.slider.cloneNode(true);
      this.querySelector(':scope > hdt-slider')?.remove();
      this.querySelector('[data-rhl-loading]')?.remove();
      this.insertBefore(slider, this.querySelector('[data-rhl-toolbar]'));
      imageArrows(this);
      this.dataset.state = batch.state;
      this.dataset.products = batch.products;
      this.dataset.categories = batch.categories;
      delete this.dataset.error;
    }
    observe() {
      this.observer?.disconnect();
      if (!('IntersectionObserver' in window)) return this.load();
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) { this.observer.disconnect(); this.load(); }
      }, { rootMargin: '1200px 0px' });
      this.observer.observe(this);
    }
    async load() {
      if (this.busy) return;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.updateControls();
      this.announce('Loading a new selection…');
      try {
        const config = JSON.parse(this.querySelector('[data-rhl-config]').textContent);
        const root = this.dataset.root.replace(/\/?$/, '/');
        const poolURL = (handle, count) => {
          const pages = Math.max(1, Math.ceil(Math.min(25000, count) / 250));
          const url = new URL(root + 'collections/' + handle, location.origin);
          url.searchParams.set('section_id', 'rhl-recommendation-pool');
          url.searchParams.set('sort_by', 'title-ascending');
          url.searchParams.set('page', String(1 + Math.floor(random() * pages)));
          return url.href;
        };
        const urls = [poolURL('all', config.all)];
        const categories = config.collections.filter(c => c.count > 0);
        if (categories.length) {
          const category = categories[Math.floor(random() * categories.length)];
          urls.push(poolURL(category.handle, category.count));
        }
        const results = await Promise.allSettled(urls.map(getPool));
        const rows = results.filter(r => r.status === 'fulfilled').flatMap(r => r.value);
        const seen = read(SEEN_KEY, []);
        const selected = selectProducts(rows, Number(this.dataset.limit) || 10, this.dataset.current, seen);
        if (!selected.length) throw new Error('No candidates');
        const url = new URL(root + 'search', location.origin);
        url.searchParams.set('section_id', 'rhl-recommendation-cards');
        url.searchParams.set('type', 'product');
        url.searchParams.set('q', selected.map(p => p[0]).join('|'));
        const doc = await getHTML(url);
        const templates = doc.querySelectorAll('template[data-rhl-card]');
        if (!templates.length) throw new Error('No live cards');
        const slider = this.querySelector('[data-rhl-slider]').content.cloneNode(true);
        const container = slider.querySelector('[data-rhl-cards]');
        const rendered = [];
        for (const item of selected) {
          const template = [...templates].find(t => t.dataset.rhlCard === item[0]);
          if (!template) continue;
          container.appendChild(template.content.cloneNode(true));
          rendered.push(item);
        }
        if (!rendered.length) throw new Error('No matching live cards');
        if (!this.isConnected) return;
        const batch = {
          slider, state: 'ready', products: rendered.map(p => p[0]).join('|'),
          categories: [...new Set(rendered.map(p => p[1]))].join('|')
        };
        this.showBatch(batch);
        this.history.push(batch);
        this.announce('New selection loaded. Use Previous picks to go back.');
        write(SEEN_KEY, [...seen.filter(h => !rendered.some(p => p[0] === h)), ...rendered.map(p => p[0])].slice(-60));
      } catch (error) {
        this.dataset.error = error.message;
        // A transient request failure keeps an existing set, otherwise uses Shopify's original recommendations.
        if (!this.querySelector(':scope > hdt-slider')) {
          try {
            const doc = await getHTML(this.dataset.fallback);
            const slider = doc.querySelector('product-recommendations hdt-slider');
            if (!slider?.querySelector('hdt-card-product')) throw new Error('No fallback');
            if (!this.isConnected) return;
            const batch = { slider, state: 'fallback', products: '', categories: '' };
            this.showBatch(batch);
            this.history.push(batch);
            this.announce('Recommendations loaded.');
          } catch (_) {
            this.closest('.shopify-section')?.setAttribute('hidden', '');
            this.dataset.state = 'unavailable';
          }
        } else this.announce('Could not refresh. Your current selection is still here. Please try again.');
      } finally {
        this.busy = false; this.setAttribute('aria-busy', 'false');
        this.updateControls();
      }
    }
  }
  customElements.define('rhl-random-recommendations', RandomRecommendations);
}
